// Modale de comparaison des trois moteurs de rendu d'arbre (Topola,
// family-chart, Balkan) sur les mêmes données, chacun avec son export PDF/PNG.
// But : choisir visuellement le moteur qui rend le mieux avant de l'adopter.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Box, Typography,
  ToggleButton, ToggleButtonGroup, Autocomplete, TextField, Switch, FormControlLabel,
  Divider, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Close as CloseIcon, PictureAsPdf as PdfIcon, Image as ImageIcon,
} from '@mui/icons-material';
import { Membre, Relation } from '../types';
import { firstRootId, toPersonNodes } from '../utils/genealogyModel';
import { EngineExportHandle } from './engines/engineTypes';
import TopolaChart, { TopolaKind } from './engines/TopolaChart';
import FamilyChartView from './engines/FamilyChartView';
import BalkanChart from './engines/BalkanChart';

type Engine = 'topola' | 'familychart' | 'balkan';

interface Props {
  open: boolean;
  onClose: () => void;
  membres: Membre[];
  relations: Relation[];
  defaultRootId: number | null;
}

const ENGINES: { key: Engine; label: string; note: string }[] = [
  { key: 'familychart', label: 'family-chart', note: 'D3, look moderne interactif (ISC, open-source). Export via image du SVG.' },
  { key: 'topola', label: 'Topola', note: 'Généalogie native (Apache-2.0, open-source) : sablier / descendants / ascendance. Export via image du SVG.' },
  { key: 'balkan', label: 'BALKAN', note: 'Rendu soigné mais licence commerciale (filigrane en éval) et capricieux sur ce jeu de données.' },
];

const TOPOLA_KINDS: { key: TopolaKind; label: string }[] = [
  { key: 'hourglass', label: 'Sablier' },
  { key: 'descendants', label: 'Descendants' },
  { key: 'ancestors', label: 'Ascendance' },
];

const EngineCompareDialog: React.FC<Props> = ({ open, onClose, membres, relations, defaultRootId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [engine, setEngine] = useState<Engine>('familychart');
  const [kind, setKind] = useState<TopolaKind>('hourglass');
  const [rootId, setRootId] = useState<number | null>(defaultRootId);
  const [dark, setDark] = useState(false);

  const engineRef = useRef<EngineExportHandle>(null);

  useEffect(() => {
    if (!open) return;
    setRootId(defaultRootId ?? firstRootId(membres, relations));
    const nodes = toPersonNodes(membres, relations);
    /* eslint-disable no-console */
    console.log('[DIAG] membres reçus =', membres.length, '| relations =', relations.length, '| adaptés =', nodes.length);
    const k = nodes.find(n => n.id === 45);
    console.log('[DIAG] Kévin liens → father=', k?.father, 'mother=', k?.mother,
      'spouses=', JSON.stringify(k?.spouses), 'children=', JSON.stringify(k?.children));
    const withLinks = nodes.filter(n => n.father != null || n.mother != null).length;
    console.log('[DIAG] nœuds avec au moins un parent =', withLinks, '/', nodes.length);
    /* eslint-enable no-console */
  }, [open, defaultRootId, membres, relations]);

  const rootMembre = useMemo(() => membres.find(m => m.id === rootId) ?? null, [membres, rootId]);
  const meta = ENGINES.find(e => e.key === engine)!;

  // Diagnostic : ce que l'adaptateur produit réellement à partir des props reçues.
  const diag = useMemo(() => {
    const nodes = toPersonNodes(membres, relations);
    const linked = nodes.filter(n => n.father != null || n.mother != null || n.spouses.length || n.children.length).length;
    return { total: membres.length, adapted: nodes.length, linked };
  }, [membres, relations]);

  const engineView = () => {
    switch (engine) {
      case 'topola':
        return <TopolaChart ref={engineRef} membres={membres} relations={relations} rootId={rootId} kind={kind} />;
      case 'familychart':
        return <FamilyChartView ref={engineRef} membres={membres} relations={relations} />;
      case 'balkan':
        return <BalkanChart ref={engineRef} membres={membres} relations={relations} dark={dark} />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, height: isMobile ? '100%' : '88vh' } }}>
      <DialogTitle sx={{ fontWeight: 700, pr: 6 }}>
        Comparer les moteurs d'arbre
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup value={engine} exclusive size="small" onChange={(_, v) => v && setEngine(v)}>
            {ENGINES.map(e => (
              <ToggleButton key={e.key} value={e.key} sx={{ textTransform: 'none', px: 1.6 }}>{e.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>

          {engine === 'topola' && (
            <ToggleButtonGroup value={kind} exclusive size="small" onChange={(_, v) => v && setKind(v)}>
              {TOPOLA_KINDS.map(k => (
                <ToggleButton key={k.key} value={k.key} sx={{ textTransform: 'none' }}>{k.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {engine === 'topola' && (
            <Autocomplete
              size="small" sx={{ minWidth: 220 }}
              options={membres} value={rootMembre}
              onChange={(_, m) => setRootId(m?.id ?? null)}
              getOptionLabel={m => `${m.prenom} ${m.nom}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={params => <TextField {...params} label="Personne de départ" />}
            />
          )}

          {engine === 'balkan' && (
            <FormControlLabel
              control={<Switch checked={dark} onChange={e => setDark(e.target.checked)} size="small" />}
              label={<Typography variant="body2">Sombre</Typography>}
            />
          )}
        </Box>

        <Typography variant="caption" color="text.secondary">{meta.note}</Typography>
        <Typography variant="caption" sx={{ color: diag.linked < 3 ? 'error.main' : 'success.main', fontWeight: 600 }}>
          Diagnostic : {diag.total} membres reçus → {diag.adapted} adaptés, {diag.linked} avec ≥ 1 lien familial.
        </Typography>
        <Divider />

        {/* Zone de rendu du moteur (clé = remonte le composant à chaque changement) */}
        <Box sx={{ flex: 1, minHeight: 380, overflow: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: dark && engine === 'balkan' ? '#111' : 'background.default' }}>
          <Box key={`${engine}-${kind}-${rootId}-${dark}`} sx={{ width: '100%', height: '100%' }}>
            {engineView()}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Fermer</Button>
        <Button onClick={() => engineRef.current?.exportPng()} variant="outlined" size="small" startIcon={<ImageIcon />}>PNG</Button>
        <Button onClick={() => engineRef.current?.exportPdf()} variant="contained" size="small" disableElevation startIcon={<PdfIcon />}>PDF</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EngineCompareDialog;
